import React, { useState, useRef, useEffect } from 'react';
import { debounce } from 'lodash';
import styled from 'styled-components';
import { Modal, Button, message } from 'antd';
import { SquareUploader, SquareUploaderProps } from './SquareUploader';
import { UploadButton } from './UploadButton';
import { LineUploader, LineUploaderProps } from './LineUploader';
import { FilterForm } from './FilterForm';
import { FILE_LIMIT, FILE_TYPE_REGEXP } from '../../help/file';
import { TinyAudioPlayer } from './TinyAudioPlayer';

import { dropFileUploaderEffect } from '../../help/effects';

const messageWarn = debounce(function(...args: any[]) {
  message.warn(args);
}, 1000);

const Title = styled.div`
  font-size: 20px;
  color: #3f3f3f;
  display: flex;
  align-items: flex-end;
`;

const Description = styled.div`
  color: #9b9b9b;
  font-size: 14px;
  margin-left: 10px;
`;

const LineSeperator = styled.div`
  width: 1px;
  height: 510px;
  background: #a8a8a878;
`;

const TITLES = {
  image: '上传图片',
  video: '上传视频',
  audio: '上传音频',
  document: '上传文档',
  animation: '上传动画',
};

const DESCRIPTIONS = {
  image: 'PNG/JPG/JPEG',
  video: 'MP4',
  audio: 'MP3/WAV',
  document: 'PDF/DOC/DOCX/PPT/PPTX/XLS/XLSX/SRT/LRC',
  animation: 'ZIP',
};

const FILE_ACCEPT_STRING = {
  image: 'image/png,image/jpg,image/jpeg',
  video: 'video/mp4',
  audio: 'audio/mp3,audio/wav',
  document: '',
  animation: 'application/zip',
};

interface UploadProps {
  type: 'image' | 'audio' | 'document' | 'video' | 'animation';
  closeModal: () => void;
  filterData: any;
  subject: string;
  onUploadSuccess: (data?: any) => void;
  initFiles: any[];
  autoOpenDir: boolean;
  usage?: 'common' | 'teaching_resource';
  courseUuid?: string;
  supportBulkUpload: boolean;
  shouldPassAllFiles?: boolean;
  maxFiles?: number;
  maxFileSize?: number;
  uploadScene?: 'SMART_PPT_SPLIT';
}

const UPLOADER = {
  image: React.memo((props: SquareUploaderProps) => {
    return (
      <SquareUploader {...props}>
        {props.url && <img style={{ maxWidth: '100%', maxHeight: '100%' }} src={props.url} />}
      </SquareUploader>
    );
  }),
  video: React.memo((props: SquareUploaderProps) => {
    return (
      <SquareUploader {...props}>
        {props.url && <video style={{ maxWidth: '100%', maxHeight: '100%' }} src={props.url} />}
      </SquareUploader>
    );
  }),
  audio: React.memo((props: LineUploaderProps) => {
    return (
      <LineUploader {...props}>
        <TinyAudioPlayer url={props.url} width={30} height={30} />
      </LineUploader>
    );
  }),
  document: React.memo((props: LineUploaderProps) => {
    return <LineUploader {...props} />;
  }),
  animation: React.memo((props: LineUploaderProps) => {
    return <LineUploader {...props} />;
  }),
};

let session = Math.random()
  .toString(36)
  .substring(3);

export function Upload(props: UploadProps) {
  const Uploader = UPLOADER[props.type];
  const [files, setFiles] = useState<any[]>(props.initFiles);
  const formRef = useRef<FilterForm>(null);
  const [couldCommit, setCouldCommit] = useState(false);
  const { supportBulkUpload, shouldPassAllFiles=false, maxFiles=20 } = props;

  const dropTarget = useRef(null);

  const safetySetFiles = (files: any[]) => {
    if (files.length > maxFiles) {
      messageWarn(`单次上传最多只能添加${maxFiles}个文件`);
    }
    const _files = files.slice(0, maxFiles);
    setFiles(_files);
    return _files;
  };

  useEffect(() => {
    return dropFileUploaderEffect(
      dropTarget.current,
      FILE_TYPE_REGEXP[props.type],
      1024 * 1024 * FILE_LIMIT
    );
  }, []);

  useEffect(() => {
    safetySetFiles(files.concat(props.initFiles));
  }, [props.initFiles.map(v => v.id).join('')]);

  useEffect(() => {
    session = Math.random()
      .toString(36)
      .substring(3);
  }, []);

  const submit = () => {
    if (files.length === 0) {
      message.info('尚未添加文件');
      return;
    }

    if (files.filter(v => !v.uuid).length > 0) {
      message.info('请先删除失败文件，然后点击确定');
      return;
    }

    const formData = formRef.current.getFieldValue(true);
    if (typeof nx.get(formData, 'type') === 'undefined') return;
    if (
      props.usage === 'teaching_resource' &&
      typeof nx.get(formData, 'disableDownload') === 'undefined'
    ) {
      message.error('请先选择: 是否支持下载');
      return;
    }

    if (formData) {
      let url;
      let method;
      let bodyParams;
      if (props.usage === 'teaching_resource') {
        url =
          window.alo7Api.prefix.host +
          `/api/v1/course_${window.alo7Nav.subject}/${props.courseUuid}/resources`;
        method = 'POST';
        bodyParams = { biz: 'TEACHING_RESOURCE' };
      } else {
        url = window.alo7Api.prefix.host + `/api/v1/resources_${props.subject}`;
        method = 'PUT';
      }

      window.alo7Api
        .fetch(url, {
          method,
          body: JSON.stringify({
            ...bodyParams,
            ...formData,
            ...(props.uploadScene ? { uploadScene: props.uploadScene } : {}),
            resources: files.map(v => ({ name: v.name, uuid: v.uuid })),
          }),
        })
        .then(() => {
          message.success('上传成功');
          if (shouldPassAllFiles) {
            props.onUploadSuccess(files);
          } else {
            props.onUploadSuccess(files[0]);
          }
        })
        .catch(error => message.error(`上传失败:${error.message}`));
    }
  };

  const UploadButtonType = ['image', 'video'].includes(props.type) ? 'square' : 'line';

  const errorFiles = files.filter(v => !v.uuid).length > 0;
  return (
    <div>
      <Title>
        <span>{TITLES[props.type]}</span>
        <Description>{DESCRIPTIONS[props.type]}</Description>
        {supportBulkUpload && (
          <Description style={{ marginLeft: '10px' }}>{`批量上传每次最多不超过${maxFiles}个！`}</Description>
        )}
      </Title>
      <div style={{ display: 'flex', marginTop: '12px' }}>
        <div
          ref={dropTarget}
          style={{
            width: '520px',
            height: '495px',
            display: 'flex',
            flexWrap: 'wrap',
            marginRight: '12px',
            alignContent: 'flex-start',
            flex: '0 1 auto',
            overflow: 'auto',
          }}>
          {UploadButtonType === 'line' && (supportBulkUpload !== false || files.length === 0) && (
            <UploadButton
              autoOpenDirectory={props.autoOpenDir}
              type={UploadButtonType}
              acceptRegexp={FILE_TYPE_REGEXP[props.type]}
              acceptString={FILE_ACCEPT_STRING[props.type]}
              maxSize={ props.maxFileSize || 1024 * 1024 * FILE_LIMIT}
              onload={newFiles => {
                safetySetFiles(files.concat(newFiles));
              }}
              style={{ marginBottom: '12px' }}
            />
          )}
          {files.map(file =>
            React.createElement(Uploader, {
              sessionId: session,
              ...file,
              type: props.type,
              key: file.id,
              subject: props.subject,
              onNameChange: (value: string, id: string) => {
                const newFiles = files.map(file => {
                  if (file.id === id) {
                    return { ...file, name: value };
                  } else {
                    return file;
                  }
                });
                safetySetFiles(newFiles);
              },
              onFileCancel: (id: string) => {
                const _files = safetySetFiles(files.filter(v => v.id !== id));
                if (_files && !!_files.length) {
                  const flag = _files.reduce(
                    (flag: boolean, file: any) => flag && (!!file.url && !!file.uuid),
                    true
                  );
                  setCouldCommit(flag);
                } else {
                  setCouldCommit(false);
                }
              },
              onUUIDChange: (id: string, uuid: string) => {
                const newFiles = files.map(file => {
                  if (file.id === id) {
                    return { ...file, uuid };
                  } else {
                    return file;
                  }
                });
                safetySetFiles(newFiles);
              },
              onUrlChange: (id: string, url: string) => {
                const newFiles = files.map(file => {
                  if (file.id === id) {
                    return { ...file, url };
                  } else {
                    return file;
                  }
                });
                safetySetFiles(newFiles);
              },
              onUploadStart: () => {
                setCouldCommit(false);
              },
              onUploadEnd: () => {
                setCouldCommit(true);
              },
            })
          )}
          {UploadButtonType === 'square' && (supportBulkUpload !== false || files.length === 0) && (
            <UploadButton
              autoOpenDirectory={props.autoOpenDir}
              type={UploadButtonType}
              acceptRegexp={FILE_TYPE_REGEXP[props.type]}
              acceptString={FILE_ACCEPT_STRING[props.type]}
              maxSize={1024 * 1024 * FILE_LIMIT}
              onload={newFiles => {
                safetySetFiles(files.concat(newFiles));
              }}
            />
          )}
        </div>
        <LineSeperator />
        <FilterForm
          data={props.filterData}
          type={props.type}
          useAs={'form'}
          ref={formRef}
          usage={props.usage}
        />
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          style={{ marginRight: '15px' }}
          onClick={() => {
            if (files.length > 0) {
              Modal.confirm({
                cancelText: '取消',
                content: '是否确认取消上传',
                okText: '确定',
                onCancel: (fn: any) => fn(),
                onOk: (fn: any) => {
                  fn();
                  props.closeModal();
                },
              });
            } else {
              props.closeModal();
            }
          }}>
          取消
        </Button>
        <Button disabled={!couldCommit || errorFiles} type={'primary'} onClick={submit}>
          确定
        </Button>
      </div>
    </div>
  );
}
